/** Plan option codes for IndusInd Nippon Life Nishchit Pension (product 22070). */
export const PENSION_PLAN_OPTION_CODES = [
  "option_1",
  "option_2",
  "option_3",
  "option_4",
  "option_5",
];

/** Plan option codes for Nishchit Pension Pro (product 22074). */
export const PRO_PLAN_OPTION_CODES = [
  "option_1",
  "option_2",
  "option_3",
  "option_4",
  "option_5",
  "option_6",
];

/** Display labels aligned with bulk API `planOptions` keys (same as UI copy). */
export const PENSION_PLAN_OPTION_LABELS = {
  option_1: "Single Life Annuity",
  option_2: "Single Life Annuity with Return of Premium plus CI/TPD benefit",
  option_3: "Single Life Annuity with Return of Balance of Premium",
  option_4: "Joint Life Annuity",
  option_5: "Joint Life Annuity with Return of Premium plus CI/TPD Benefit",
};

export const PRO_PLAN_OPTION_LABELS = {
  option_1: "Single Life Annuity",
  option_2: "Single Life Annuity with Return of Premium plus CI/TPD benefit",
  option_3: "Single Life Annuity for Certain Period of 10 years",
  option_4: "Single Life Annuity for Certain Period of 15 years",
  option_5: "Joint Life Annuity",
  option_6: "Joint Life Annuity with Return of Premium plus CI/TPD Benefit",
};

export const getNischitPlanOptionCodesForProduct = (productCode) =>
  String(productCode) === "22074" ? PRO_PLAN_OPTION_CODES : PENSION_PLAN_OPTION_CODES;

export const NISCHIT_PRODUCT_CONFIG = {
  "22070": {
    name: "Indusind Nippon Life Nishchit Pension",
    planOptions: [
      { id: "option_1", label: "Single Life Annuity" },
      {
        id: "option_2",
        label: "Single Life Annuity with Return of Premium plus CI/TPD benefit",
      },
      { id: "option_3", label: "Single Life Annuity with Return of Balance of Premium" },
      { id: "option_4", label: "Joint Life Annuity" },
      {
        id: "option_5",
        label: "Joint Life Annuity with Return of Premium plus CI/TPD Benefit",
      },
    ],
    pptOptions: [2, 3, 5, 6, 7, 8, 10],
    pptToDp: {
      "2": [5, 6, 7, 8, 9, 10, 15],
      "3": [5, 6, 7, 8, 9, 10, 15],
      "5": [5, 6, 7, 8, 9, 10, 15],
      "6": [6, 7, 8, 9, 10, 15],
      "7": [7, 8, 9, 10, 15],
      "8": [8, 9, 10, 15],
      "10": [10, 15],
    },
  },
  "22074": {
    name: "Indusind Nippon Life Nishchit Pension Pro",
    planOptions: [
      { id: "option_1", label: "Single Life Annuity" },
      {
        id: "option_2",
        label: "Single Life Annuity with Return of Premium plus CI/TPD benefit",
      },
      { id: "option_3", label: "Single Life Annuity for Certain Period of 10 years" },
      { id: "option_4", label: "Single Life Annuity for Certain Period of 15 years" },
      { id: "option_5", label: "Joint Life Annuity" },
      {
        id: "option_6",
        label: "Joint Life Annuity with Return of Premium plus CI/TPD Benefit",
      },
    ],
    pptOptions: [1],
    pptLabel: { "1": "Single Pay" },
    dpOptions: [1, 2, 3, 4, 5, 7],
  },
};

const normalizeBooleanLike = (value) => {
  const raw = String(value ?? "").trim().toLowerCase();
  return (
    raw === "true" ||
    raw === "yes" ||
    raw === "1" ||
    raw === "option19854214"
  );
};

export const resolveNischitProductCode = (leadFormData = {}) =>
  normalizeBooleanLike(leadFormData?.isSinglePay) ? "22074" : "22070";

export const getNischitProductConfig = (productCode) =>
  NISCHIT_PRODUCT_CONFIG[String(productCode)] || NISCHIT_PRODUCT_CONFIG["22070"];

const toPositiveInt = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
};

/** Allowed deferment (DP) years for a PPT on the given Nishchit product. */
export const getAllowedDpForPpt = (productCode, ppt) => {
  const config = getNischitProductConfig(productCode);
  const fromMap = config?.pptToDp?.[String(ppt)];
  if (Array.isArray(fromMap) && fromMap.length) {
    return fromMap.map(Number);
  }
  if (Array.isArray(config?.dpOptions) && config.dpOptions.length) {
    return config.dpOptions.map(Number);
  }
  return [];
};

/**
 * Resolve PPT / DP for Excel. Never use Term `policyTerm` as DP — the 22070 rate
 * table only allows specific pairs (e.g. PPT 10 → DP 10 or 15). An out-of-table
 * DP makes Calculation!C7/C12 evaluate to #N/A.
 */
export const resolveNischitPptAndDp = (productCode, customize = {}, fallbacks = {}) => {
  const config = getNischitProductConfig(productCode);
  const pptOptions = (config?.pptOptions || [10]).map(Number);
  const requestedPpt = toPositiveInt(
    customize?.premiumPayingTerm ??
      customize?.ppt ??
      fallbacks?.premiumPayingTerm ??
      fallbacks?.ppt,
  );
  const ppt = pptOptions.includes(requestedPpt)
    ? requestedPpt
    : pptOptions[pptOptions.length - 1];
  const allowedDp = getAllowedDpForPpt(productCode, ppt);
  const requestedDp = toPositiveInt(
    customize?.withdrawalAfter ??
      customize?.dp ??
      fallbacks?.withdrawalAfter ??
      fallbacks?.dp,
  );
  const dp = allowedDp.includes(requestedDp)
    ? requestedDp
    : (allowedDp[0] ?? 15);
  return { ppt, dp };
};

/**
 * Plan rows shown for the selected main annuity type (Single Life vs Joint Life),
 * using the same rule as the quote UI: label contains "Joint Life" → joint bucket.
 */
export const getNischitVisiblePlanOptions = (productCode, isJointLifeMainPlan) => {
  const config = getNischitProductConfig(productCode);
  return (config?.planOptions || []).filter((opt) => {
    const isJointRow = String(opt?.label || "")
      .toLowerCase()
      .includes("joint life");
    return isJointLifeMainPlan ? isJointRow : !isJointRow;
  });
};

export const getNischitVisiblePlanOptionIds = (productCode, isJointLifeMainPlan) =>
  getNischitVisiblePlanOptions(productCode, isJointLifeMainPlan).map((o) => o.id);

const NP_PRODUCT_COVERAGE_BY_OPTION = {
  option_1: { ProductCode: "DAP", CoverageType: "DAP1" },
  option_2: { ProductCode: "DAP", CoverageType: "DAP2" },
  option_3: { ProductCode: "DAP", CoverageType: "DAP3" },
  option_4: { ProductCode: "DAJ", CoverageType: "DAJ1" },
  option_5: { ProductCode: "DAJ", CoverageType: "DAJ2" },
};

const NPP_PRODUCT_COVERAGE_BY_OPTION = {
  option_1: { ProductCode: "PAP", CoverageType: "PAP1" },
  option_2: { ProductCode: "PAP", CoverageType: "PAP2" },
  option_3: { ProductCode: "PAP", CoverageType: "PAP3" },
  option_4: { ProductCode: "PAP", CoverageType: "PAP4" },
  option_5: { ProductCode: "PAJ", CoverageType: "PAJ1" },
  option_6: { ProductCode: "PAJ", CoverageType: "PAJ2" },
};

const withContractCoverageFields = (codes) => {
  if (!codes?.ProductCode || !codes?.CoverageType) {
    return {};
  }
  return {
    ProductCode: codes.ProductCode,
    CoverageType: codes.CoverageType,
    Coverage_Code: codes.CoverageType,
    ContractType: codes.ProductCode,
  };
};

const normalizePlanOptionId = (raw) => {
  const value = String(raw ?? "").trim().toLowerCase();
  return /^option_[1-6]$/.test(value) ? value : "";
};

const codesFromPlanLabel = (productCode, selectedPlan) => {
  const pid = String(productCode || "22070").trim();
  const labels = pid === "22074" ? PRO_PLAN_OPTION_LABELS : PENSION_PLAN_OPTION_LABELS;
  const byOption = pid === "22074" ? NPP_PRODUCT_COVERAGE_BY_OPTION : NP_PRODUCT_COVERAGE_BY_OPTION;
  const needle = String(selectedPlan ?? "").trim().toLowerCase();
  if (!needle) return null;
  const ranked = Object.entries(labels).sort(
    (a, b) => String(b[1]).length - String(a[1]).length,
  );
  for (const [optionId, label] of ranked) {
    if (needle === String(label).toLowerCase()) {
      return byOption[optionId];
    }
  }
  for (const [optionId, label] of ranked) {
    if (needle.includes(String(label).toLowerCase())) {
      return byOption[optionId];
    }
  }
  return null;
};

/**
 * Life Asia ProductCode / CoverageType for BizOps, decided on quote from the selected plan option.
 * ContractType = ProductCode; Coverage_Code = CoverageType.
 */
export const resolveNischitProductAndCoverageCodes = ({
  productCode,
  planOption,
  selectedPlan,
} = {}) => {
  const pid = String(productCode || "22070").trim();
  const byOption = pid === "22074" ? NPP_PRODUCT_COVERAGE_BY_OPTION : NP_PRODUCT_COVERAGE_BY_OPTION;
  const optionId = normalizePlanOptionId(planOption);
  const fromOption = optionId ? byOption[optionId] : null;
  const fromLabel = codesFromPlanLabel(pid, selectedPlan);
  return withContractCoverageFields(fromOption || fromLabel);
};

/**
 * Joint secondary annuitant is “complete” when all three fields are filled and either:
 * - user has saved the modal once (`secondaryAnnuitantDetailsSaved`), or
 * - values are not the untouched modal defaults (1994-01-01 + Male + Son together).
 *
 * Note: we must not treat every "male" / "son" as a placeholder — those are valid real values.
 */
export const isNischitSecondaryAnnuitantComplete = (customize = {}) => {
  const dobRaw = customize?.secondaryAnnuitantDob;
  const dobStr = dobRaw == null ? "" : String(dobRaw);
  const dobNormalized = dobStr.slice(0, 10);

  const genderRaw = customize?.secondaryAnnuitantGender;
  const genderStr = genderRaw == null ? "" : String(genderRaw).trim().toLowerCase();

  const relationRaw = customize?.secondaryAnnuitantRelation;
  const relationStr =
    relationRaw == null ? "" : String(relationRaw).trim().toLowerCase();

  const hasAll =
    Boolean(dobNormalized) && Boolean(genderStr) && Boolean(relationStr);
  if (!hasAll) return false;

  if (customize?.secondaryAnnuitantDetailsSaved === true) {
    return true;
  }

  const isUntouchedModalDefaults =
    dobNormalized === "1994-01-01" &&
    genderStr === "male" &&
    relationStr === "son";

  return !isUntouchedModalDefaults;
};
